import type { ProspectMatterType } from './enums';

export const RoleName = {
    BASIC: 'Basic',
    LAWYER: 'Lawyer',
    CLERK: 'Clerk',
    NOTARY: 'Notary',
    ADMIN: 'Admin',
    SUPER_ADMIN: 'Super Admin',
} as const;

export type RoleName = (typeof RoleName)[keyof typeof RoleName];

export const RoleId = {
    CLERK: 106000,
    LAWYER: 106001,
    ORGANIZATIONAL_ADMIN: 106002,
    SUPER_ADMIN: 106003,
    BASIC: 106004,
    ADMIN: 106005,
    NOTARY: 106006,
} as const;

export type RoleId = (typeof RoleId)[keyof typeof RoleId];

/** Primary job roles that can be assigned to a user (in addition to Basic). */
export const PRIMARY_ROLE_IDS = [RoleId.LAWYER, RoleId.CLERK, RoleId.NOTARY] as const;

export type PrimaryRoleId = (typeof PRIMARY_ROLE_IDS)[number];

export const PRIMARY_ROLE_OPTIONS = [
    { roleId: RoleId.LAWYER, name: RoleName.LAWYER },
    { roleId: RoleId.CLERK, name: RoleName.CLERK },
    { roleId: RoleId.NOTARY, name: RoleName.NOTARY },
] as const;

export function isPrimaryRoleId(roleId: number): roleId is PrimaryRoleId {
    return (PRIMARY_ROLE_IDS as readonly number[]).includes(roleId);
}

export const ExportType = {
    TREEFORT_COMPLETED: 'TreefortCompleted',
    LEXO_COMPLETED: 'LexoCompleted',
    STANDALONE_IDV_COMPLETED: 'StandAloneIDVCompleted',
    ALL_COMPLETED: 'AllCompleted',
    ALL: 'All',
} as const;

export type ExportType = (typeof ExportType)[keyof typeof ExportType];

export const ADDITIONAL_DOCUMENT_TITLE = "Lawyer's Additional Document";
/** How many additional documents a prospect's intake email can include */
export const MAX_ADDITIONAL_DOCUMENTS = 10;

export const INTAKE_SUMMARY_UPLOAD_TITLE = 'Intake Summary';

export const INTAKE_SUMMARY_DOCUMENT_DISPLAY_NAME = 'Intake summary document';

export const LEGACY_INTAKE_SUMMARY_TYPE_OF_DOCUMENT = 'IntakeInfo';

export const DEFAULT_UNSIGNED_DOCUMENT_DISPLAY_NAME = 'Legal Document';

export const DEFAULT_SIGNED_DOCUMENT_DISPLAY_NAME = 'Signed Legal Document';

interface EmailParts {
    subject: string;
    bodyBeforeLink: string;
    bodyAfterLink: string;
}

export const DEFAULT_EMAIL_BODY_AFTER_LINK =
    'If you have any questions, feel free to reach out.' as const;

const DEFAULT_INCORPORATION_EMAIL_BODY_PARTS: EmailParts = {
    subject: 'Please complete your intake form for incorporation',
    bodyBeforeLink:
        'To move forward with the incorporation, we require the completion of the intake form linked below. Please ensure that all intake questions are completed as accurately as possible.',
    bodyAfterLink: DEFAULT_EMAIL_BODY_AFTER_LINK,
} as const;

export const INTAKE_EMAIL_BODY_DEFAULTS: Record<ProspectMatterType, EmailParts> = {
    Sale: {
        subject: 'Your Sale - Getting Started',
        bodyBeforeLink:
            'Thank you for choosing our firm to assist with your upcoming sale. We will take care of the legal details to ensure a seamless closing.\n\nTo get your file underway, please take a moment to complete the intake form below. This allows us to confirm the necessary details and begin preparing your documentation.',
        bodyAfterLink:
            'If you have any questions about the form or any aspect of the transaction, please feel free to reply to this email. We are happy to help.\n\nThank you again for your trust in our firm.',
    },
    Purchase: {
        subject: 'Your Purchase - Getting Started',
        bodyBeforeLink:
            'Thank you for entrusting us with your upcoming purchase. We look forward to ensuring a smooth closing on your behalf.\n\nTo begin preparing your file, please complete the intake form below at your earliest convenience. The information you provide allows us to get started right away and helps avoid any delays as we approach closing.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    Refinance: {
        subject: 'Your Refinance - Getting Started',
        bodyBeforeLink:
            'Thank you for selecting our firm to handle your refinance. We have received your file and are ready to get started.\n\nPlease complete the intake form below when you have a moment. The information you provide helps us confirm key details and keep everything on track for a timely closing.',
        bodyAfterLink:
            "Should you have any questions about the form or the refinance process, please don't hesitate to reply to this email. We are here to assist.\n\nWe look forward to working with you.",
    },
    PrivateLender: {
        subject: 'Please complete your initial client intake form',
        bodyBeforeLink:
            'To serve you quicker, we require the completion of the intake form linked below.',
        bodyAfterLink: DEFAULT_EMAIL_BODY_AFTER_LINK,
    },
    TitleTransfer: {
        subject: 'Title Transfer - Getting Started',
        bodyBeforeLink:
            'To begin your title transfer matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    BareTransfer: {
        subject: 'Bare Transfer - Getting Started',
        bodyBeforeLink:
            'To begin your bare transfer matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    BareTransferAndRefinance: {
        subject: 'Bare Transfer and Refinance - Getting Started',
        bodyBeforeLink:
            'To begin your bare transfer and refinance matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    ILA: {
        subject: 'Independent Legal Advice - Getting Started',
        bodyBeforeLink:
            'To begin your independent legal advice matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    PurchaseAndSale: {
        subject: 'Purchase & Sale - Getting Started',
        bodyBeforeLink:
            'To begin your purchase & sale matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    Transfer: {
        subject: 'Transfer - Getting Started',
        bodyBeforeLink:
            'To begin your transfer matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    TransferAndRefinance: {
        subject: 'Transfer & Refinance - Getting Started',
        bodyBeforeLink:
            'To begin your transfer & refinance matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    TransferOfTitle: {
        subject: 'Transfer of Title - Getting Started',
        bodyBeforeLink:
            'To begin your transfer of title matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    SurvivorshipApplication: {
        subject: 'Survivorship Application - Getting Started',
        bodyBeforeLink:
            'To begin your survivorship application matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    ForProfitIncorporation: DEFAULT_INCORPORATION_EMAIL_BODY_PARTS,
    NotForProfitIncorporation: DEFAULT_INCORPORATION_EMAIL_BODY_PARTS,
    Corporate: {
        subject: 'Corporate - Getting Started',
        bodyBeforeLink:
            'To begin your corporate matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    EstatePlanning: {
        subject: 'Estate Planning - Getting Started',
        bodyBeforeLink:
            'To begin your estate planning matter, please complete the intake form linked below. This helps us collect the information we need to draft your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    EstateAdministration: {
        subject: 'Estate Administration - Getting Started',
        bodyBeforeLink:
            'To begin your estate administration matter, please complete the intake form linked below. This helps us collect the information we need to draft your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    PowerOfAttorney: {
        subject: 'Power of Attorney - Getting Started',
        bodyBeforeLink:
            'To begin your power of attorney matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    Civil: {
        subject: 'Civil - Getting Started',
        bodyBeforeLink:
            'To begin your civil matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    PersonalInjury: {
        subject: 'Personal Injury - Getting Started',
        bodyBeforeLink:
            'To begin your personal injury matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    EmploymentLaw: {
        subject: 'Employment Law - Getting Started',
        bodyBeforeLink:
            'To begin your employment law matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    LandlordTenant: {
        subject: 'Landlord & Tenant - Getting Started',
        bodyBeforeLink:
            'To begin your landlord & tenant matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    MotorVehicleAccident: {
        subject: 'Motor Vehicle Accident - Getting Started',
        bodyBeforeLink:
            'To begin your motor vehicle accident matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    Litigation: {
        subject: 'Litigation - Getting Started',
        bodyBeforeLink:
            'To begin your litigation matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    Family: {
        subject: 'Family - Getting Started',
        bodyBeforeLink:
            'To begin your family matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    Divorce: {
        subject: 'Divorce - Getting Started',
        bodyBeforeLink:
            'To begin your divorce matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    CohabitationAgreement: {
        subject: 'Cohabitation Agreement - Getting Started',
        bodyBeforeLink:
            'To begin your cohabitation agreement matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    SeparationAgreement: {
        subject: 'Separation Agreement - Getting Started',
        bodyBeforeLink:
            'To begin your separation agreement matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    ParentingAgreement: {
        subject: 'Parenting Agreement - Getting Started',
        bodyBeforeLink:
            'To begin your parenting agreement matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    SpousalSupport: {
        subject: 'Spousal Support - Getting Started',
        bodyBeforeLink:
            'To begin your spousal support matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    MarriageCohabitation: {
        subject: 'Marriage/Cohabitation - Getting Started',
        bodyBeforeLink:
            'To begin your marriage/cohabitation matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    FertilityLaw: {
        subject: 'Fertility Law - Getting Started',
        bodyBeforeLink:
            'To begin your fertility law matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    MotionToChange: {
        subject: 'Motion to Change - Getting Started',
        bodyBeforeLink:
            'To begin your motion to change matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    SeparationAndDivorce: {
        subject: 'Separation & Divorce - Getting Started',
        bodyBeforeLink:
            'To begin your separation & divorce matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    IndependentLegalAdvice: {
        subject: 'Independent Legal Advice - Getting Started',
        bodyBeforeLink:
            'To begin your independent legal advice matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    Criminal: {
        subject: 'Criminal - Getting Started',
        bodyBeforeLink:
            'To begin your criminal matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    ImpairedDriving: {
        subject: 'Impaired Driving - Getting Started',
        bodyBeforeLink:
            'To begin your impaired driving matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    Assault: {
        subject: 'Assault - Getting Started',
        bodyBeforeLink:
            'To begin your assault matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    TheftAndFraud: {
        subject: 'Theft & Fraud - Getting Started',
        bodyBeforeLink:
            'To begin your theft & fraud matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    Immigration: {
        subject: 'Immigration - Getting Started',
        bodyBeforeLink:
            'To begin your immigration matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    TemporaryStatus: {
        subject: 'Temporary Status - Getting Started',
        bodyBeforeLink:
            'To begin your temporary status matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    PermanentResidence: {
        subject: 'Permanent Residence - Getting Started',
        bodyBeforeLink:
            'To begin your permanent residence matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    Citizenship: {
        subject: 'Citizenship - Getting Started',
        bodyBeforeLink:
            'To begin your citizenship matter, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
    GeneralIntake: {
        subject: 'General Intake - Getting Started',
        bodyBeforeLink:
            'To begin your intake, please complete the intake form linked below. This helps us collect the information we need to prepare your documents accurately.',
        bodyAfterLink:
            "Should you have any questions about the form or the process, please don't hesitate to reply to this email. We're happy to assist.\n\nWe look forward to working with you.",
    },
} as const;

export const DEFAULT_REMINDER_SUBJECT = 'Reminder to complete your client intake form' as const;

export const DEFAULT_POST_INTAKE_SUBJECT = 'Update Your Intake Form If Needed' as const;

const DEFAULT_INCORPORATION_REMINDER_SUBJECT =
    'Reminder to complete your client intake form for incorporation' as const;

export const INTAKE_REMINDER_EMAIL_BODY_DEFAULTS: Record<ProspectMatterType, EmailParts> = {
    Sale: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.Sale,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    Purchase: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.Purchase,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    Refinance: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.Refinance,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    PrivateLender: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.PrivateLender,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    TitleTransfer: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.TitleTransfer,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    BareTransfer: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.BareTransfer,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    BareTransferAndRefinance: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.BareTransferAndRefinance,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    ILA: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.ILA,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    PurchaseAndSale: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.PurchaseAndSale,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    Transfer: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.Transfer,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    TransferAndRefinance: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.TransferAndRefinance,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    TransferOfTitle: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.TransferOfTitle,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    SurvivorshipApplication: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.SurvivorshipApplication,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    ForProfitIncorporation: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.ForProfitIncorporation,
        subject: DEFAULT_INCORPORATION_REMINDER_SUBJECT,
    },
    NotForProfitIncorporation: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.NotForProfitIncorporation,
        subject: DEFAULT_INCORPORATION_REMINDER_SUBJECT,
    },
    Corporate: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.Corporate,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    EstatePlanning: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.EstatePlanning,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    EstateAdministration: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.EstateAdministration,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    PowerOfAttorney: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.PowerOfAttorney,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    Civil: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.Civil,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    PersonalInjury: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.PersonalInjury,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    EmploymentLaw: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.EmploymentLaw,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    LandlordTenant: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.LandlordTenant,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    MotorVehicleAccident: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.MotorVehicleAccident,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    Litigation: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.Litigation,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    Family: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.Family,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    Divorce: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.Divorce,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    CohabitationAgreement: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.CohabitationAgreement,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    SeparationAgreement: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.SeparationAgreement,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    ParentingAgreement: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.ParentingAgreement,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    SpousalSupport: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.SpousalSupport,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    MarriageCohabitation: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.MarriageCohabitation,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    FertilityLaw: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.FertilityLaw,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    MotionToChange: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.MotionToChange,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    SeparationAndDivorce: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.SeparationAndDivorce,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    IndependentLegalAdvice: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.IndependentLegalAdvice,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    Criminal: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.Criminal,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    ImpairedDriving: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.ImpairedDriving,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    Assault: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.Assault,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    TheftAndFraud: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.TheftAndFraud,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    Immigration: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.Immigration,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    TemporaryStatus: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.TemporaryStatus,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    PermanentResidence: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.PermanentResidence,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    Citizenship: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.Citizenship,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
    GeneralIntake: {
        ...INTAKE_EMAIL_BODY_DEFAULTS.GeneralIntake,
        subject: DEFAULT_REMINDER_SUBJECT,
    },
} as const;

export const TITLE_TRANSFER_LAW_FIRM_IDS: readonly number[] = [
    2027, // Francispillai Law
    2051, // Korman & Company
];
export const ILA_LAW_FIRM_ID = 2026; // King Law
export const RITA_LAW_FIRM_ID = 1094; // Rita Asangarani Law Firm
export const LENNOX_LAW_FIRM_ID = 2033; // Lennox & Penney LLP
export const MARIE_LAW_FIRM_ID = 2036; // Marie-Michèle Lanteigne
export const PETER_CULLEN_LAW_FIRM_ID = 2039; // Peter & Cullen
export const RAMSAY_LAW_FIRM_ID = 1104; // Ramsay Law Office Professional Corporation
export const POTESTIO_LAW_FIRM_ID = 2034; // Potestio Law
export const MCGUINTY_LAW_FIRM_ID = 2049; // McGuinty Law Offices Professional Corporation

/**
 * Matter types a law firm cannot select.
 * Firm-specific matter types are only offered to one firm; this is the inverse.
 */
export const DISABLED_MATTER_TYPES_BY_LAW_FIRM_ID: Partial<
    Record<number, readonly ProspectMatterType[]>
> = {
    [POTESTIO_LAW_FIRM_ID]: ['ForProfitIncorporation'], // Potestio Law
    [MCGUINTY_LAW_FIRM_ID]: ['ForProfitIncorporation'], // McGuinty Law Offices
};

export const INTAKE_TIME_SAVED_MINUTES = 120;
export const CONNECT_TIME_SAVED_MINUTES = 30;

// Assumed blended hourly rate used to convert time saved into dollars saved for the firm.
export const DOLLARS_SAVED_PER_HOUR = 37;

export const MILESTONE_THRESHOLDS = {
    timeSavedHours: [100, 250, 500, 1000, 2500],
    intakesCompleted: [50, 100, 250, 500, 1000],
} as const;

export const PROVINCE_TIMEZONE_MAP: Record<string, string> = {
    Ontario: 'America/Toronto',
    Alberta: 'America/Edmonton',
    'British Columbia': 'America/Vancouver',
    Saskatchewan: 'America/Regina',
    Manitoba: 'America/Winnipeg',
    'Nova Scotia': 'America/Halifax',
    'New Brunswick': 'America/Moncton',
    'Prince Edward Island': 'America/Halifax',
    'Newfoundland and Labrador': 'America/St_Johns',
    Quebec: 'America/Toronto',
    'Northwest Territories': 'America/Yellowknife',
    Nunavut: 'America/Rankin_Inlet',
    Yukon: 'America/Whitehorse',
};
